import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store';
import { fetchRecommendations, clearRecommendations, updateEpicEstimate, updateTeamAdjustment } from '@/store/slices/scenariosSlice';
import {
  GlassCard,
  GlassBadge,
  GlassLoadingSpinner,
  GlassAlert,
  GlassButton,
  SectionReveal,
  MetricCard,
} from '@/design-system';
import {
  AlertTriangle,
  TrendingDown,
  DollarSign,
  CheckCircle,
  Users,
  ArrowRight,
  Plus,
  Loader2,
  ChevronDown,
  ChevronRight,
  Check,
} from 'lucide-react';
import type {
  OverCommittedRecommendation,
  UnderUtilizedRecommendation,
  BudgetFitRecommendation,
} from '@/types';

interface RecommendationsPanelProps {
  scenarioId: string;
  programId?: string;
}

interface OverCommittedSectionProps {
  items: OverCommittedRecommendation[];
  selectedTeamIds: Set<string>;
  expandedTeamIds: Set<string>;
  selectedRollOffs: Map<string, Set<string>>;
  isApplying: boolean;
  onToggleTeam: (teamId: string) => void;
  onToggleExpand: (teamId: string) => void;
  onToggleRollOff: (teamId: string, epicId: string) => void;
  onApplyRollOffs: () => void;
}

interface UnderUtilizedSectionProps {
  items: UnderUtilizedRecommendation[];
  selectedTeamIds: Set<string>;
  expandedTeamIds: Set<string>;
  selectedAddOptions: Map<string, string>;
  isApplying: boolean;
  onToggleTeam: (teamId: string) => void;
  onToggleExpand: (teamId: string) => void;
  onSelectAddOption: (teamId: string, costProfileId: string) => void;
  onApplyAdditions: () => void;
}

const formatCurrency = (value: number): string => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

const SelectionCheckbox: React.FC<{
  checked: boolean;
  onChange: () => void;
  className?: string;
}> = ({ checked, onChange, className = '' }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onChange(); }}
    className={`w-5 h-5 rounded border flex items-center justify-center transition-colors shrink-0 ${
      checked
        ? 'bg-blue-500 border-blue-500 text-white'
        : 'border-[var(--border-subtle)] hover:border-blue-400'
    } ${className}`}
  >
    {checked && <Check className="w-3 h-3" />}
  </button>
);

const OverCommittedSection: React.FC<OverCommittedSectionProps> = ({
  items,
  selectedTeamIds,
  expandedTeamIds,
  selectedRollOffs,
  isApplying,
  onToggleTeam,
  onToggleExpand,
  onToggleRollOff,
  onApplyRollOffs,
}) => {
  if (items.length === 0) return null;

  // Calculate totals for selected roll-offs
  let totalSelectedRollOffs = 0;
  let totalSavings = 0;
  for (const [teamId, epicIds] of selectedRollOffs) {
    const team = items.find(t => t.teamId === teamId);
    if (!team) continue;
    for (const epicId of epicIds) {
      const suggestion = team.rollOffSuggestions.find(s => s.epicId === epicId);
      if (suggestion) {
        totalSelectedRollOffs++;
        totalSavings += suggestion.costSaved;
      }
    }
  }

  return (
    <SectionReveal>
      <GlassCard className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-red-500/10">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">
            Over-Committed Teams
          </h3>
          <GlassBadge variant="danger">{items.length} team{items.length !== 1 ? 's' : ''}</GlassBadge>
        </div>

        <div className="space-y-4">
          {items.map((team) => {
            const isTeamSelected = selectedTeamIds.has(team.teamId);
            const isExpanded = expandedTeamIds.has(team.teamId);
            const teamRollOffs = selectedRollOffs.get(team.teamId);

            return (
              <div
                key={team.teamId}
                className={`p-4 rounded-lg border transition-all ${
                  isTeamSelected
                    ? 'border-blue-500/50 bg-red-500/5 ring-2 ring-blue-500/50'
                    : 'border-red-500/20 bg-red-500/5'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <SelectionCheckbox
                      checked={isTeamSelected}
                      onChange={() => onToggleTeam(team.teamId)}
                    />
                    <div>
                      <span className="font-medium text-foreground">{team.teamName}</span>
                      <span className="text-sm text-muted-foreground ml-2">({team.programName})</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <GlassBadge variant="danger">{team.currentUtilization}% utilized</GlassBadge>
                    <span className="text-sm text-muted-foreground">
                      {team.excessWeeks} excess TWs
                    </span>
                    <button
                      onClick={() => onToggleExpand(team.teamId)}
                      className="p-1 rounded hover:bg-[var(--bg-tertiary)] transition-colors text-muted-foreground"
                    >
                      {isExpanded
                        ? <ChevronDown className="w-4 h-4" />
                        : <ChevronRight className="w-4 h-4" />
                      }
                    </button>
                  </div>
                </div>

                {/* Expanded: Current Allocations */}
                {isExpanded && team.rollOffSuggestions.length > 0 && (
                  <div className="mb-3 p-3 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-subtle)]">
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      Current Allocations
                    </p>
                    <div className="space-y-1">
                      {team.rollOffSuggestions.map((suggestion) => (
                        <div
                          key={`alloc-${suggestion.epicId}`}
                          className="flex items-center justify-between p-2 rounded bg-[var(--bg-secondary)] text-sm"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-foreground">{suggestion.epicName}</span>
                            <GlassBadge variant="default">{suggestion.category}</GlassBadge>
                          </div>
                          <span className="text-muted-foreground">
                            {suggestion.allocatedTeamWeeks} TWs allocated
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {team.rollOffSuggestions.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      Roll-off suggestions (by priority):
                    </p>
                    <div className="space-y-2">
                      {team.rollOffSuggestions.map((suggestion) => {
                        const isRollOffSelected = teamRollOffs?.has(suggestion.epicId) ?? false;

                        return (
                          <div
                            key={suggestion.epicId}
                            className={`flex items-center justify-between p-2 rounded transition-all ${
                              isRollOffSelected
                                ? 'bg-emerald-500/10 ring-1 ring-emerald-500/30'
                                : 'bg-[var(--bg-tertiary)]'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <SelectionCheckbox
                                checked={isRollOffSelected}
                                onChange={() => onToggleRollOff(team.teamId, suggestion.epicId)}
                              />
                              <ArrowRight className="w-3 h-3 text-muted-foreground" />
                              <span className="text-sm text-foreground">{suggestion.epicName}</span>
                              <GlassBadge variant="default">P{suggestion.priority}</GlassBadge>
                              <span className="text-xs text-muted-foreground">
                                {suggestion.category}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                              <span className="text-muted-foreground">
                                {suggestion.allocatedTeamWeeks} TWs
                              </span>
                              <span className="text-emerald-400">
                                {formatCurrency(suggestion.costSaved)} saved
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-3 pt-3 border-t border-[var(--border-subtle)] flex justify-between text-sm">
                  <span className="text-muted-foreground">Total potential savings</span>
                  <span className="font-medium text-emerald-400">
                    {formatCurrency(team.totalPotentialSavings)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Apply Roll-offs Action Bar */}
        {totalSelectedRollOffs > 0 && (
          <div className="mt-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between">
            <span className="text-sm text-emerald-400">
              {totalSelectedRollOffs} roll-off{totalSelectedRollOffs !== 1 ? 's' : ''} selected
              {' '}(save ~{formatCurrency(totalSavings)})
            </span>
            <GlassButton variant="primary" size="sm" onClick={onApplyRollOffs} disabled={isApplying}>
              {isApplying ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Apply Roll-offs
            </GlassButton>
          </div>
        )}
      </GlassCard>
    </SectionReveal>
  );
};

const UnderUtilizedSection: React.FC<UnderUtilizedSectionProps> = ({
  items,
  selectedTeamIds,
  expandedTeamIds,
  selectedAddOptions,
  isApplying,
  onToggleTeam,
  onToggleExpand,
  onSelectAddOption,
  onApplyAdditions,
}) => {
  if (items.length === 0) return null;

  // Calculate totals for selected additions
  let selectedAddCount = 0;
  let totalAdditionalCost = 0;
  for (const [teamId, costProfileId] of selectedAddOptions) {
    const team = items.find(t => t.teamId === teamId);
    if (!team) continue;
    const option = team.addMemberOptions.find(o => o.costProfileId === costProfileId);
    if (option) {
      selectedAddCount++;
      totalAdditionalCost += option.additionalAnnualCost;
    }
  }

  return (
    <SectionReveal>
      <GlassCard className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-amber-500/10">
            <TrendingDown className="w-5 h-5 text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">
            Under-Utilized Teams
          </h3>
          <GlassBadge variant="warning">{items.length} team{items.length !== 1 ? 's' : ''}</GlassBadge>
        </div>

        <div className="space-y-4">
          {items.map((team) => {
            const isTeamSelected = selectedTeamIds.has(team.teamId);
            const isExpanded = expandedTeamIds.has(team.teamId);
            const selectedCostProfileId = selectedAddOptions.get(team.teamId);

            return (
              <div
                key={team.teamId}
                className={`p-4 rounded-lg border transition-all ${
                  isTeamSelected
                    ? 'border-blue-500/50 bg-amber-500/5 ring-2 ring-blue-500/50'
                    : 'border-amber-500/20 bg-amber-500/5'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <SelectionCheckbox
                      checked={isTeamSelected}
                      onChange={() => onToggleTeam(team.teamId)}
                    />
                    <div>
                      <span className="font-medium text-foreground">{team.teamName}</span>
                      <span className="text-sm text-muted-foreground ml-2">({team.programName})</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <GlassBadge variant="warning">{team.currentUtilization}% utilized</GlassBadge>
                    <span className="text-sm text-muted-foreground">
                      {team.spareCapacityWeeks} spare TWs
                    </span>
                    <button
                      onClick={() => onToggleExpand(team.teamId)}
                      className="p-1 rounded hover:bg-[var(--bg-tertiary)] transition-colors text-muted-foreground"
                    >
                      {isExpanded
                        ? <ChevronDown className="w-4 h-4" />
                        : <ChevronRight className="w-4 h-4" />
                      }
                    </button>
                  </div>
                </div>

                {/* Expanded: Details */}
                {isExpanded && (
                  <div className="mb-3 p-3 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-subtle)]">
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      Team Details
                    </p>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-muted-foreground">Utilization: </span>
                        <span className="text-foreground font-medium">{team.currentUtilization}%</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Spare Capacity: </span>
                        <span className="text-foreground font-medium">{team.spareCapacityWeeks} TWs</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Program: </span>
                        <span className="text-foreground font-medium">{team.programName}</span>
                      </div>
                    </div>
                  </div>
                )}

                {team.addMemberOptions.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Users className="w-3 h-3" /> Add members to fill capacity:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {team.addMemberOptions.map((option) => {
                        const isOptionSelected = selectedCostProfileId === option.costProfileId;

                        return (
                          <button
                            key={option.costProfileId}
                            onClick={() => onSelectAddOption(team.teamId, option.costProfileId)}
                            className={`p-3 rounded text-sm text-left transition-all ${
                              isOptionSelected
                                ? 'bg-blue-500/10 ring-2 ring-blue-500/50 border border-blue-500/30'
                                : 'bg-[var(--bg-tertiary)] hover:bg-[var(--bg-secondary)] border border-transparent'
                            }`}
                          >
                            <div className="font-medium text-foreground">{option.costProfileName}</div>
                            <div className="text-muted-foreground mt-1">
                              +{option.additionalMembers} member{option.additionalMembers !== 1 ? 's' : ''}
                            </div>
                            <div className="text-muted-foreground">
                              +{option.additionalCapacityWeeks} TWs capacity
                            </div>
                            <div className="text-amber-400 mt-1">
                              {formatCurrency(option.additionalAnnualCost)}/yr
                            </div>
                            {isOptionSelected && (
                              <div className="mt-1 flex items-center gap-1 text-blue-400 text-xs">
                                <Check className="w-3 h-3" /> Selected
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {team.newTeamOptions.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Create new team:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {team.newTeamOptions.slice(0, 3).map((option, idx) => (
                        <div
                          key={`${option.costProfileId}-${option.suggestedTeamSize}-${idx}`}
                          className="p-3 rounded bg-[var(--bg-tertiary)] text-sm"
                        >
                          <div className="font-medium text-foreground">
                            {option.suggestedTeamSize}-person {option.costProfileName}
                          </div>
                          <div className="text-muted-foreground mt-1">
                            {option.capacityWeeks} TWs capacity
                          </div>
                          <div className="text-amber-400 mt-1">
                            {formatCurrency(option.annualCost)}/yr
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Apply Additions Action Bar */}
        {selectedAddCount > 0 && (
          <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-between">
            <span className="text-sm text-blue-400">
              {selectedAddCount} team addition{selectedAddCount !== 1 ? 's' : ''} selected
              {' '}(+{formatCurrency(totalAdditionalCost)}/yr)
            </span>
            <GlassButton variant="primary" size="sm" onClick={onApplyAdditions} disabled={isApplying}>
              {isApplying ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Apply Additions
            </GlassButton>
          </div>
        )}
      </GlassCard>
    </SectionReveal>
  );
};

const BudgetFitSection: React.FC<{ data: BudgetFitRecommendation }> = ({ data }) => {
  return (
    <SectionReveal>
      <GlassCard className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-blue-500/10">
            <DollarSign className="w-5 h-5 text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">
            Budget Fit Analysis
          </h3>
          <span className="text-sm text-muted-foreground">{data.programName}</span>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="p-3 rounded-lg bg-[var(--bg-tertiary)]">
            <div className="text-sm text-muted-foreground">Labor Budget</div>
            <div className="text-lg font-semibold text-foreground">{formatCurrency(data.laborBudget)}</div>
          </div>
          <div className="p-3 rounded-lg bg-[var(--bg-tertiary)]">
            <div className="text-sm text-muted-foreground">Current Team Cost</div>
            <div className="text-lg font-semibold text-foreground">{formatCurrency(data.currentTeamCost)}</div>
          </div>
          <div className="p-3 rounded-lg bg-[var(--bg-tertiary)]">
            <div className="text-sm text-muted-foreground">Budget Headroom</div>
            <div className={`text-lg font-semibold ${data.budgetHeadroom >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {data.budgetHeadroom >= 0 ? '+' : ''}{formatCurrency(data.budgetHeadroom)}
            </div>
          </div>
        </div>

        {data.fitsWithinBudget.length > 0 && (
          <div className="mb-4">
            <p className="text-sm font-medium text-emerald-400 mb-2 flex items-center gap-1">
              <CheckCircle className="w-4 h-4" /> Fits Within Budget ({data.fitsWithinBudget.length})
            </p>
            <div className="space-y-1">
              {data.fitsWithinBudget.map((epic) => (
                <div
                  key={epic.epicId}
                  className="flex items-center justify-between p-2 rounded bg-emerald-500/5 border border-emerald-500/10"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground">{epic.epicName}</span>
                    <GlassBadge variant="default">{epic.category}</GlassBadge>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-muted-foreground">{epic.requestedTeamWeeks} TWs</span>
                    <span className="text-foreground">{formatCurrency(epic.estimatedCost)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.doesNotFit.length > 0 && (
          <div>
            <p className="text-sm font-medium text-red-400 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> Does Not Fit ({data.doesNotFit.length})
            </p>
            <div className="space-y-2">
              {data.doesNotFit.map((epic) => {
                const alternative = data.costProfileAlternatives.find(a => a.epicId === epic.epicId);
                return (
                  <div
                    key={epic.epicId}
                    className="p-3 rounded bg-red-500/5 border border-red-500/10"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-foreground">{epic.epicName}</span>
                        <GlassBadge variant="default">{epic.category}</GlassBadge>
                      </div>
                      <span className="text-sm text-foreground">{formatCurrency(epic.estimatedCost)}</span>
                    </div>
                    {alternative && (
                      <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                        <div className="p-2 rounded bg-[var(--bg-tertiary)]">
                          <div className="text-muted-foreground">Onshore</div>
                          <div className="text-foreground">{formatCurrency(alternative.onshoreCost)}</div>
                        </div>
                        <div className="p-2 rounded bg-[var(--bg-tertiary)]">
                          <div className="text-muted-foreground">Offshore</div>
                          <div className="text-emerald-400">{formatCurrency(alternative.offshoreCost)}</div>
                        </div>
                        <div className="p-2 rounded bg-[var(--bg-tertiary)]">
                          <div className="text-muted-foreground">Blended</div>
                          <div className="text-foreground">{formatCurrency(alternative.blendedCost)}</div>
                        </div>
                      </div>
                    )}
                    {alternative && (
                      <p className="text-xs text-blue-400 mt-2">{alternative.recommendation}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </GlassCard>
    </SectionReveal>
  );
};

export const RecommendationsPanel: React.FC<RecommendationsPanelProps> = ({
  scenarioId,
  programId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { recommendations, recommendationsLoading, recommendationsError } = useSelector(
    (state: RootState) => state.scenarios
  );

  // Selection state
  const [selectedTeamIds, setSelectedTeamIds] = useState<Set<string>>(new Set());
  const [expandedTeamIds, setExpandedTeamIds] = useState<Set<string>>(new Set());
  const [selectedRollOffs, setSelectedRollOffs] = useState<Map<string, Set<string>>>(new Map());
  const [selectedAddOptions, setSelectedAddOptions] = useState<Map<string, string>>(new Map());
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    dispatch(fetchRecommendations({ scenarioId, programId }));
    return () => {
      dispatch(clearRecommendations());
    };
  }, [dispatch, scenarioId, programId]);

  // --- Selection handlers ---

  const toggleTeamSelection = (teamId: string) => {
    setSelectedTeamIds(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
        // Also clear roll-offs for this team when deselecting
        setSelectedRollOffs(prevRollOffs => {
          const nextRollOffs = new Map(prevRollOffs);
          nextRollOffs.delete(teamId);
          return nextRollOffs;
        });
        // Also clear add options for this team when deselecting
        setSelectedAddOptions(prevOpts => {
          const nextOpts = new Map(prevOpts);
          nextOpts.delete(teamId);
          return nextOpts;
        });
      } else {
        next.add(teamId);
        // Auto-select all roll-offs when selecting team (over-committed)
        const overCommittedTeam = recommendations?.overCommitted.find(t => t.teamId === teamId);
        if (overCommittedTeam && overCommittedTeam.rollOffSuggestions.length > 0) {
          setSelectedRollOffs(prevRollOffs => {
            const nextRollOffs = new Map(prevRollOffs);
            const epicIds = new Set(overCommittedTeam.rollOffSuggestions.map(s => s.epicId));
            nextRollOffs.set(teamId, epicIds);
            return nextRollOffs;
          });
        }
      }
      return next;
    });
  };

  const toggleExpandTeam = (teamId: string) => {
    setExpandedTeamIds(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  };

  const toggleRollOff = (teamId: string, epicId: string) => {
    setSelectedRollOffs(prev => {
      const next = new Map(prev);
      const epicIds = new Set(next.get(teamId) ?? []);
      if (epicIds.has(epicId)) {
        epicIds.delete(epicId);
        if (epicIds.size === 0) {
          next.delete(teamId);
        } else {
          next.set(teamId, epicIds);
        }
      } else {
        epicIds.add(epicId);
        next.set(teamId, epicIds);
      }
      return next;
    });
  };

  const selectAddOption = (teamId: string, costProfileId: string) => {
    setSelectedAddOptions(prev => {
      const next = new Map(prev);
      if (next.get(teamId) === costProfileId) {
        // Deselect if already selected
        next.delete(teamId);
      } else {
        next.set(teamId, costProfileId);
      }
      return next;
    });
  };

  const clearAllSelections = () => {
    setSelectedTeamIds(new Set());
    setSelectedRollOffs(new Map());
    setSelectedAddOptions(new Map());
  };

  // --- Apply handlers ---

  const handleApplyRollOffs = async () => {
    setIsApplying(true);
    try {
      const promises: Promise<unknown>[] = [];
      for (const [teamId, epicIds] of selectedRollOffs) {
        const team = recommendations?.overCommitted.find(t => t.teamId === teamId);
        if (!team) continue;
        for (const epicId of epicIds) {
          const suggestion = team.rollOffSuggestions.find(s => s.epicId === epicId);
          if (!suggestion) continue;
          promises.push(
            dispatch(updateEpicEstimate({
              scenarioId,
              epicId: suggestion.epicId,
              adjustedTeamWeeks: 0,
            })).unwrap()
          );
        }
      }
      await Promise.all(promises);
      clearAllSelections();
      dispatch(fetchRecommendations({ scenarioId, programId }));
    } catch (error) {
      console.error('Failed to apply roll-offs:', error);
    } finally {
      setIsApplying(false);
    }
  };

  const handleApplyAdditions = async () => {
    setIsApplying(true);
    try {
      const promises: Promise<unknown>[] = [];
      for (const [teamId, costProfileId] of selectedAddOptions) {
        const team = recommendations?.underUtilized.find(t => t.teamId === teamId);
        if (!team) continue;
        const option = team.addMemberOptions.find(o => o.costProfileId === costProfileId);
        if (!option) continue;
        promises.push(
          dispatch(updateTeamAdjustment({
            scenarioId,
            teamId,
            adjustedMemberCount: option.additionalMembers,
          })).unwrap()
        );
      }
      await Promise.all(promises);
      clearAllSelections();
      dispatch(fetchRecommendations({ scenarioId, programId }));
    } catch (error) {
      console.error('Failed to apply additions:', error);
    } finally {
      setIsApplying(false);
    }
  };

  // --- Computed values for summary bar ---

  let totalSelectedRollOffsCount = 0;
  let estimatedSavings = 0;
  for (const [teamId, epicIds] of selectedRollOffs) {
    const team = recommendations?.overCommitted.find(t => t.teamId === teamId);
    if (!team) continue;
    for (const epicId of epicIds) {
      const suggestion = team.rollOffSuggestions.find(s => s.epicId === epicId);
      if (suggestion) {
        totalSelectedRollOffsCount++;
        estimatedSavings += suggestion.costSaved;
      }
    }
  }

  let estimatedAddCost = 0;
  for (const [teamId, costProfileId] of selectedAddOptions) {
    const team = recommendations?.underUtilized.find(t => t.teamId === teamId);
    if (!team) continue;
    const option = team.addMemberOptions.find(o => o.costProfileId === costProfileId);
    if (option) {
      estimatedAddCost += option.additionalAnnualCost;
    }
  }

  // --- Render ---

  if (recommendationsLoading) {
    return (
      <div className="flex justify-center py-12">
        <GlassLoadingSpinner size="lg" label="Analyzing scenario..." />
      </div>
    );
  }

  if (recommendationsError) {
    return <GlassAlert variant="error" title="Error" message={recommendationsError} />;
  }

  if (!recommendations) {
    return null;
  }

  const hasRecommendations =
    recommendations.overCommitted.length > 0 ||
    recommendations.underUtilized.length > 0 ||
    recommendations.budgetFit !== null;

  const hasAnySelection = selectedTeamIds.size > 0 || selectedRollOffs.size > 0 || selectedAddOptions.size > 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <SectionReveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Over-Committed"
            value={recommendations.summary.overCommittedTeamCount}
            subtitle="teams above capacity"
            icon={<AlertTriangle className="w-5 h-5" />}
            iconColor="rose"
          />
          <MetricCard
            title="Under-Utilized"
            value={recommendations.summary.underUtilizedTeamCount}
            subtitle="teams below 70%"
            icon={<TrendingDown className="w-5 h-5" />}
            iconColor="amber"
          />
          <MetricCard
            title="Potential Savings"
            value={formatCurrency(recommendations.summary.totalPotentialSavings)}
            subtitle="from roll-offs"
            icon={<DollarSign className="w-5 h-5" />}
            iconColor="emerald"
          />
          <MetricCard
            title="Budget Status"
            value={recommendations.summary.budgetStatus?.replace('_', ' ') ?? 'N/A'}
            subtitle={programId ? 'for selected program' : 'select a program'}
            icon={<CheckCircle className="w-5 h-5" />}
            iconColor={
              recommendations.summary.budgetStatus === 'UNDER_BUDGET' ? 'emerald' :
              recommendations.summary.budgetStatus === 'NEAR_BUDGET' ? 'amber' :
              recommendations.summary.budgetStatus === 'OVER_BUDGET' ? 'rose' : 'blue'
            }
          />
        </div>
      </SectionReveal>

      {/* Selection Summary Bar */}
      {hasAnySelection && (
        <SectionReveal>
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-blue-400 font-medium">
                {selectedTeamIds.size} team{selectedTeamIds.size !== 1 ? 's' : ''} selected
              </span>
              {totalSelectedRollOffsCount > 0 && (
                <span className="text-emerald-400">
                  {totalSelectedRollOffsCount} roll-offs (save ~{formatCurrency(estimatedSavings)})
                </span>
              )}
              {selectedAddOptions.size > 0 && (
                <span className="text-amber-400">
                  {selectedAddOptions.size} addition{selectedAddOptions.size !== 1 ? 's' : ''} (+{formatCurrency(estimatedAddCost)}/yr)
                </span>
              )}
            </div>
            <GlassButton variant="ghost" size="sm" onClick={clearAllSelections}>
              Clear All
            </GlassButton>
          </div>
        </SectionReveal>
      )}

      {/* All Clear State */}
      {!hasRecommendations && (
        <SectionReveal>
          <GlassCard className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-foreground mb-1">All Clear!</h3>
            <p className="text-muted-foreground">
              No over-committed or under-utilized teams detected.
              {!programId && ' Select a program to see budget fit analysis.'}
            </p>
          </GlassCard>
        </SectionReveal>
      )}

      {/* Sections */}
      <OverCommittedSection
        items={recommendations.overCommitted}
        selectedTeamIds={selectedTeamIds}
        expandedTeamIds={expandedTeamIds}
        selectedRollOffs={selectedRollOffs}
        isApplying={isApplying}
        onToggleTeam={toggleTeamSelection}
        onToggleExpand={toggleExpandTeam}
        onToggleRollOff={toggleRollOff}
        onApplyRollOffs={handleApplyRollOffs}
      />
      <UnderUtilizedSection
        items={recommendations.underUtilized}
        selectedTeamIds={selectedTeamIds}
        expandedTeamIds={expandedTeamIds}
        selectedAddOptions={selectedAddOptions}
        isApplying={isApplying}
        onToggleTeam={toggleTeamSelection}
        onToggleExpand={toggleExpandTeam}
        onSelectAddOption={selectAddOption}
        onApplyAdditions={handleApplyAdditions}
      />
      {recommendations.budgetFit && <BudgetFitSection data={recommendations.budgetFit} />}
    </div>
  );
};

export default RecommendationsPanel;
